import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, Check, Heart, Sparkles, Coffee } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import ProfilePhotoUploader from '@/components/ProfilePhotoUploader';
import api from '@/lib/api';

const VALUES = [
  "Entrepreneurship", "Wellness", "Faith", "Culture", 
  "Adventure", "Career", "Creativity", "Social Impact"
];

const PARENTING_STYLES = [
  { value: "gentle", label: "Gentle", desc: "Empathy-focused, connection-first" },
  { value: "balanced", label: "Balanced", desc: "Structure with flexibility" },
  { value: "traditional", label: "Traditional", desc: "Clear boundaries and expectations" },
  { value: "free-range", label: "Free-range", desc: "Independence and exploration" }
];

const FRIENDSHIP_GOALS = [
  "Deep conversations", "Fun outings", "Parenting support", 
  "Career networking", "Workout buddy", "Wine & unwind"
];

const AVAILABILITY = [
  { value: "weekday_mornings", label: "Weekday Mornings" },
  { value: "weekday_evenings", label: "Weekday Evenings" },
  { value: "weekends", label: "Weekends" }
];

const ACTIVITIES = [
  "Coffee/brunch", "Outdoor activities", "Fitness classes",
  "Wine nights", "Cultural events", "Kids playdates"
];

// About Me options
const SOCIAL_STYLES = [
  { value: "introvert", label: "Introvert", desc: "I recharge with quiet time and prefer one-on-one connections" },
  { value: "ambivert", label: "Ambivert", desc: "I enjoy both social time and solo time in balance" },
  { value: "extrovert", label: "Extrovert", desc: "I get energy from being around people and love group settings" }
];

const IDEAL_HANGOUTS = [
  "Coffee and deep talks",
  "Group outings",
  "Outdoor adventures",
  "Low-key home hangs",
  "Wine and dinner nights"
];

const CONNECTION_FREQUENCY = [
  { value: "few_times_month", label: "A few times a month", desc: "Quality over quantity" },
  { value: "weekly", label: "Weekly", desc: "Regular connection is my jam" },
  { value: "whenever", label: "Whenever life allows", desc: "Flexible and go with the flow" }
];

const WORK_STATUS = [
  { value: "stay_at_home", label: "Stay at home mom" },
  { value: "full_time", label: "Working full time" },
  { value: "part_time", label: "Working part time" },
  { value: "entrepreneur", label: "Running my own business" },
  { value: "break", label: "Taking a break" }
];

const MOTHERHOOD_STAGES = [
  "Expecting",
  "New mom (0-1 year)",
  "Toddler years",
  "School age kids",
  "Teen years",
  "Empty nester"
];

const PARTNER_STATUS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "complicated", label: "It's complicated" }
];

const TOPICS_OF_INTEREST = [
  "Parenting", "Relationships", "Career", "Wellness",
  "Faith/Spirituality", "Culture & Travel", "Food", "Fitness",
  "Business/Entrepreneurship", "Mental Health", "Creativity/Arts", "Politics"
];

const COMMUNITY_GOALS = [
  "Someone to vent to",
  "A workout buddy",
  "A mom mentor",
  "Social plans and events",
  "Deep meaningful friendships",
  "A business network",
  "All of the above"
];

export default function OnboardingPage() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    values: [],
    parenting_style: '',
    friendship_goals: [],
    availability: [],
    activities: [],
    bio: '',
    profile_photo_url: '',
    // About Me fields
    social_style: '',
    ideal_hangout: [],
    connection_frequency: '',
    work_status: '',
    motherhood_stage: [],
    has_partner: '',
    topics_of_interest: [],
    community_goals: []
  });
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  const totalSteps = 5;
  const progress = (step / totalSteps) * 100;

  const toggleArrayValue = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const handleNext = () => {
    if (step === 1 && formData.values.length === 0) {
      toast.error('Please select at least one value');
      return;
    }
    if (step === 1 && !formData.parenting_style) {
      toast.error('Please select your parenting style');
      return;
    }
    if (step === 2 && formData.availability.length === 0) {
      toast.error('Please select your availability');
      return;
    }
    if (step === 3 && !formData.social_style) {
      toast.error('Please select your social style');
      return;
    }
    setStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      const response = await api.post('/onboarding', formData);
      updateUser(response.data);
      toast.success('Profile complete!');
      // If user arrived from a shareable event link, drop them on that event
      let redirectPath = '';
      try {
        redirectPath = localStorage.getItem('post_onboarding_redirect') || '';
        if (redirectPath) localStorage.removeItem('post_onboarding_redirect');
      } catch { /* ignore */ }
      if (redirectPath && redirectPath.startsWith('/')) {
        navigate(redirectPath);
      } else {
        navigate('/pricing');
      }
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const stepTitles = [
    "Tell us about you.",
    "What are you looking for?",
    "About Me",
    "Choose your membership",
    "Complete your profile"
  ];

  return (
    <div className="min-h-screen bg-warm-white font-body">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-lg z-50 border-b border-border-subtle">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <span className="font-display text-xl text-navy">
              Cali<span className="italic-teal">Moms</span>
            </span>
            <span className="text-sm text-text-mid">Step {step} of {totalSteps}</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </div>

      {/* Content */}
      <div className="pt-32 pb-40 px-6">
        <div className="max-w-2xl mx-auto">
          <AnimatePresence mode="wait">
            {/* Step 1: About You */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
                data-testid="onboarding-step-1"
              >
                <div>
                  <h1 className="font-display text-3xl md:text-4xl text-navy mb-2">{stepTitles[0]}</h1>
                  <p className="text-text-mid">This helps us find your perfect matches.</p>
                </div>

                <div className="space-y-4">
                  <Label className="text-navy font-semibold text-base">What matters most to you? (Select up to 3)</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {VALUES.map(value => (
                      <button
                        key={value}
                        onClick={() => toggleArrayValue('values', value)}
                        disabled={formData.values.length >= 3 && !formData.values.includes(value)}
                        data-testid={`value-${value.toLowerCase()}`}
                        className={`p-3 rounded-xl text-sm font-medium transition-all ${
                          formData.values.includes(value)
                            ? 'bg-teal text-white'
                            : 'bg-white border border-seafoam text-navy hover:border-teal'
                        } ${formData.values.length >= 3 && !formData.values.includes(value) ? 'opacity-50' : ''}`}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <Label className="text-navy font-semibold text-base">Your parenting style</Label>
                  <div className="grid md:grid-cols-2 gap-3">
                    {PARENTING_STYLES.map(style => (
                      <button
                        key={style.value}
                        onClick={() => setFormData(prev => ({ ...prev, parenting_style: style.value }))}
                        data-testid={`style-${style.value}`}
                        className={`p-4 rounded-xl text-left transition-all ${
                          formData.parenting_style === style.value
                            ? 'bg-teal text-white'
                            : 'bg-white border border-seafoam text-navy hover:border-teal'
                        }`}
                      >
                        <span className="font-semibold block">{style.label}</span>
                        <span className={`text-sm ${formData.parenting_style === style.value ? 'text-white/80' : 'text-text-mid'}`}>
                          {style.desc}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: What You're Looking For */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
                data-testid="onboarding-step-2"
              >
                <div>
                  <h1 className="font-display text-3xl md:text-4xl text-navy mb-2">{stepTitles[1]}</h1>
                  <p className="text-text-mid">Help us match you with the right moms</p>
                </div>

                <div className="space-y-4">
                  <Label className="text-navy font-semibold text-base">Friendship goals</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {FRIENDSHIP_GOALS.map(goal => (
                      <button
                        key={goal}
                        onClick={() => toggleArrayValue('friendship_goals', goal)}
                        data-testid={`goal-${goal.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`p-3 rounded-xl text-sm font-medium transition-all ${
                          formData.friendship_goals.includes(goal)
                            ? 'bg-teal text-white'
                            : 'bg-white border border-seafoam text-navy hover:border-teal'
                        }`}
                      >
                        {goal}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <Label className="text-navy font-semibold text-base">When are you available?</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {AVAILABILITY.map(time => (
                      <button
                        key={time.value}
                        onClick={() => toggleArrayValue('availability', time.value)}
                        data-testid={`availability-${time.value}`}
                        className={`p-4 rounded-xl text-sm font-medium transition-all ${
                          formData.availability.includes(time.value)
                            ? 'bg-teal text-white'
                            : 'bg-white border border-seafoam text-navy hover:border-teal'
                        }`}
                      >
                        {time.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <Label className="text-navy font-semibold text-base">Preferred activities</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {ACTIVITIES.map(activity => (
                      <button
                        key={activity}
                        onClick={() => toggleArrayValue('activities', activity)}
                        data-testid={`activity-${activity.toLowerCase().replace(/[\/\s]+/g, '-')}`}
                        className={`p-3 rounded-xl text-sm font-medium transition-all ${
                          formData.activities.includes(activity)
                            ? 'bg-teal text-white'
                            : 'bg-white border border-seafoam text-navy hover:border-teal'
                        }`}
                      >
                        {activity}
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 3: About Me - NEW */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
                data-testid="onboarding-step-3"
              >
                <div>
                  <h1 className="font-display text-3xl md:text-4xl text-navy mb-2">{stepTitles[2]}</h1>
                  <p className="text-text-mid">Let's get to know the real you — so we can find moms who truly get it.</p>
                </div>

                {/* Social Style */}
                <div className="space-y-4">
                  <Label className="text-navy font-semibold text-base flex items-center gap-2">
                    <Sparkles size={18} className="text-teal" />
                    How would you describe your social style?
                  </Label>
                  <div className="space-y-3">
                    {SOCIAL_STYLES.map(style => (
                      <button
                        key={style.value}
                        onClick={() => setFormData(prev => ({ ...prev, social_style: style.value }))}
                        data-testid={`social-${style.value}`}
                        className={`w-full p-4 rounded-xl text-left transition-all ${
                          formData.social_style === style.value
                            ? 'bg-teal text-white'
                            : 'bg-white border border-seafoam text-navy hover:border-teal'
                        }`}
                      >
                        <span className="font-semibold block">{style.label}</span>
                        <span className={`text-sm ${formData.social_style === style.value ? 'text-white/80' : 'text-text-mid'}`}>
                          {style.desc}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Ideal Hangout */}
                <div className="space-y-4">
                  <Label className="text-navy font-semibold text-base flex items-center gap-2">
                    <Coffee size={18} className="text-teal" />
                    What does your ideal hangout look like?
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    {IDEAL_HANGOUTS.map(hangout => (
                      <button
                        key={hangout}
                        onClick={() => toggleArrayValue('ideal_hangout', hangout)}
                        data-testid={`hangout-${hangout.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`p-3 rounded-xl text-sm font-medium transition-all ${
                          formData.ideal_hangout.includes(hangout)
                            ? 'bg-teal text-white'
                            : 'bg-white border border-seafoam text-navy hover:border-teal'
                        }`}
                      >
                        {hangout}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Connection Frequency */}
                <div className="space-y-4">
                  <Label className="text-navy font-semibold text-base">How often are you looking to connect?</Label>
                  <div className="space-y-3">
                    {CONNECTION_FREQUENCY.map(freq => (
                      <button
                        key={freq.value}
                        onClick={() => setFormData(prev => ({ ...prev, connection_frequency: freq.value }))}
                        data-testid={`frequency-${freq.value}`}
                        className={`w-full p-4 rounded-xl text-left transition-all ${
                          formData.connection_frequency === freq.value
                            ? 'bg-teal text-white'
                            : 'bg-white border border-seafoam text-navy hover:border-teal'
                        }`}
                      >
                        <span className="font-semibold block">{freq.label}</span>
                        <span className={`text-sm ${formData.connection_frequency === freq.value ? 'text-white/80' : 'text-text-mid'}`}>
                          {freq.desc}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Work Status */}
                <div className="space-y-4">
                  <Label className="text-navy font-semibold text-base">Are you currently working?</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {WORK_STATUS.map(status => (
                      <button
                        key={status.value}
                        onClick={() => setFormData(prev => ({ ...prev, work_status: status.value }))}
                        data-testid={`work-${status.value}`}
                        className={`p-3 rounded-xl text-sm font-medium transition-all ${
                          formData.work_status === status.value
                            ? 'bg-teal text-white'
                            : 'bg-white border border-seafoam text-navy hover:border-teal'
                        }`}
                      >
                        {status.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Motherhood Stage */}
                <div className="space-y-4">
                  <Label className="text-navy font-semibold text-base flex items-center gap-2">
                    <Heart size={18} className="text-teal" />
                    What stage of motherhood are you in?
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {MOTHERHOOD_STAGES.map(stage => (
                      <button
                        key={stage}
                        onClick={() => toggleArrayValue('motherhood_stage', stage)}
                        data-testid={`stage-${stage.toLowerCase().replace(/[\s\(\)-]+/g, '-')}`}
                        className={`p-3 rounded-xl text-sm font-medium transition-all ${
                          formData.motherhood_stage.includes(stage)
                            ? 'bg-teal text-white'
                            : 'bg-white border border-seafoam text-navy hover:border-teal'
                        }`}
                      >
                        {stage}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Partner Status - Optional */}
                <div className="space-y-4">
                  <Label className="text-navy font-semibold text-base">Do you have a partner/spouse? <span className="font-normal text-text-light">(optional)</span></Label>
                  <div className="grid grid-cols-3 gap-3">
                    {PARTNER_STATUS.map(status => (
                      <button
                        key={status.value}
                        onClick={() => setFormData(prev => ({ ...prev, has_partner: prev.has_partner === status.value ? '' : status.value }))}
                        data-testid={`partner-${status.value}`}
                        className={`p-3 rounded-xl text-sm font-medium transition-all ${
                          formData.has_partner === status.value
                            ? 'bg-teal text-white'
                            : 'bg-white border border-seafoam text-navy hover:border-teal'
                        }`}
                      >
                        {status.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Topics of Interest */}
                <div className="space-y-4">
                  <Label className="text-navy font-semibold text-base">What topics do you love talking about?</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {TOPICS_OF_INTEREST.map(topic => (
                      <button
                        key={topic}
                        onClick={() => toggleArrayValue('topics_of_interest', topic)}
                        data-testid={`topic-${topic.toLowerCase().replace(/[\/\s&]+/g, '-')}`}
                        className={`p-3 rounded-xl text-sm font-medium transition-all ${
                          formData.topics_of_interest.includes(topic)
                            ? 'bg-teal text-white'
                            : 'bg-white border border-seafoam text-navy hover:border-teal'
                        }`}
                      >
                        {topic}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Community Goals */}
                <div className="space-y-4">
                  <Label className="text-navy font-semibold text-base">What are you hoping to get out of this community?</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {COMMUNITY_GOALS.map(goal => (
                      <button
                        key={goal}
                        onClick={() => toggleArrayValue('community_goals', goal)}
                        data-testid={`community-${goal.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`p-3 rounded-xl text-sm font-medium transition-all ${
                          formData.community_goals.includes(goal)
                            ? 'bg-teal text-white'
                            : 'bg-white border border-seafoam text-navy hover:border-teal'
                        }`}
                      >
                        {goal}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Bio */}
                <div className="space-y-4">
                  <Label className="text-navy font-semibold text-base">A little more about you</Label>
                  <Textarea
                    value={formData.bio}
                    onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value.slice(0, 300) }))}
                    placeholder="I'm a mom of two in San Diego who loves hiking, terrible reality TV, and honest conversations over good coffee..."
                    rows={4}
                    data-testid="onboarding-bio"
                    className="rounded-xl border-seafoam focus:border-teal resize-none"
                  />
                  <p className="text-xs text-text-light text-right">{formData.bio.length}/300 characters</p>
                </div>
              </motion.div>
            )}

            {/* Step 4: Membership */}
            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
                data-testid="onboarding-step-4"
              >
                <div>
                  <h1 className="font-display text-3xl md:text-4xl text-navy mb-2">{stepTitles[3]}</h1>
                  <p className="text-text-mid">You can always upgrade later</p>
                </div>

                <div className="space-y-4">
                  {[
                    { tier: 'connect', name: 'Connect', price: 'Free', features: ['Community access + city discovery', 'Limited AI matching + messaging', 'Community marketplace', 'All gatherings free', 'Workshops at $45'] },
                    { tier: 'thrive', name: 'Thrive', price: '$39/mo', priceNote: 'or $390/yr — 2 months free', features: ['ONE workshop included every month', '30% off additional workshops', 'Priority AI matching + unlimited messaging', 'Curated Circles + members-only events', '24-hour early registration'], featured: true }
                  ].map(plan => (
                    <button
                      key={plan.tier}
                      onClick={() => setFormData(prev => ({ ...prev, membership_tier: plan.tier }))}
                      data-testid={`membership-${plan.tier}`}
                      className={`w-full p-6 rounded-2xl text-left transition-all ${
                        formData.membership_tier === plan.tier
                          ? plan.featured ? 'bg-teal text-white ring-2 ring-teal' : 'bg-white ring-2 ring-teal'
                          : plan.featured ? 'bg-teal/10 border border-teal' : 'bg-white border border-seafoam hover:border-teal'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <span className={`font-display text-xl ${formData.membership_tier === plan.tier && plan.featured ? 'text-white' : 'text-navy'}`}>
                            {plan.name}
                          </span>
                          {plan.featured && (
                            <span className={`ml-2 text-xs px-2 py-1 rounded-full ${
                              formData.membership_tier === plan.tier ? 'bg-white/20 text-white' : 'bg-teal/20 text-teal'
                            }`}>
                              Workshop Included
                            </span>
                          )}
                        </div>
                        <div className={`text-right ${formData.membership_tier === plan.tier && plan.featured ? 'text-white' : 'text-navy'}`}>
                          <div className="font-bold text-lg">{plan.price}</div>
                          {plan.priceNote && (
                            <div className={`text-[10px] ${formData.membership_tier === plan.tier && plan.featured ? 'text-white/80' : 'text-text-mid'}`}>{plan.priceNote}</div>
                          )}
                        </div>
                      </div>
                      <ul className="space-y-2">
                        {plan.features.map(f => (
                          <li key={f} className={`flex items-center gap-2 text-sm ${
                            formData.membership_tier === plan.tier && plan.featured ? 'text-white/90' : 'text-text-mid'
                          }`}>
                            <Check size={16} className={formData.membership_tier === plan.tier && plan.featured ? 'text-white' : 'text-teal'} />
                            {f}
                          </li>
                        ))}
                      </ul>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 5: Profile Photo */}
            {step === 5 && (
              <motion.div
                key="step5"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
                data-testid="onboarding-step-5"
              >
                <div>
                  <h1 className="font-display text-3xl md:text-4xl text-navy mb-2">{stepTitles[4]}</h1>
                  <p className="text-text-mid">Add a photo so your matches can recognize you (optional)</p>
                </div>

                <div className="flex justify-center">
                  <ProfilePhotoUploader
                    currentPhotoUrl={formData.profile_photo_url}
                    firstName={user?.first_name || ''}
                    lastName={user?.last_name || ''}
                    size={128}
                    onUploaded={(url) => {
                      setFormData(prev => ({ ...prev, profile_photo_url: url }));
                      updateUser({ profile_photo_url: url });
                    }}
                    onDeleted={() => {
                      setFormData(prev => ({ ...prev, profile_photo_url: '' }));
                      updateUser({ profile_photo_url: null });
                    }}
                    testIdPrefix="onboarding-profile-photo"
                  />
                </div>

                <div className="bg-cream rounded-2xl p-6">
                  <h3 className="font-semibold text-navy mb-2">Your Profile Preview</h3>
                  <p className="text-sm text-text-mid mb-4">
                    {formData.bio || "Your bio will appear here..."}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {formData.topics_of_interest.slice(0, 4).map(topic => (
                      <span key={topic} className="text-xs bg-seafoam/50 text-teal px-2 py-1 rounded-full">
                        {topic}
                      </span>
                    ))}
                    {formData.ideal_hangout.slice(0, 2).map(hangout => (
                      <span key={hangout} className="text-xs bg-teal/10 text-teal-deep px-2 py-1 rounded-full">
                        {hangout}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Footer Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-border-subtle z-50 mb-20 sm:mb-0">
        <div className="max-w-2xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={step === 1}
            className="text-navy"
          >
            <ArrowLeft size={18} className="mr-2" /> Back
          </Button>
          
          {step < totalSteps ? (
            <Button
              onClick={handleNext}
              data-testid="onboarding-next-btn"
              className="bg-teal hover:bg-teal-deep text-white rounded-full px-8"
            >
              Continue <ArrowRight size={18} className="ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleComplete}
              disabled={loading}
              data-testid="onboarding-complete-btn"
              className="bg-teal hover:bg-teal-deep text-white rounded-full px-8"
            >
              {loading ? 'Saving...' : 'Complete Profile'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
